import { useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import { INFLUENCER_TYPES, SAVED_INFLUENCERS } from "../lib/sample-data";
import { UserSquare2, Wand2, Mic, Sparkles, Tag } from "lucide-react";
import { cn } from "../lib/utils";

const GENDERS = ["Female", "Male", "Non-binary", "Custom"];
const TONES = ["Confident", "Calm", "Energetic", "Authoritative", "Playful", "Warm"];

export default function AIInfluencer() {
  const [type, setType] = useState("luxury");
  const [gender, setGender] = useState("Female");
  const [tone, setTone] = useState("Confident");
  const [niche, setNiche] = useState("Premium coaching · founders");
  const [personality, setPersonality] = useState("");

  return (
    <div data-testid="ai-influencer-page" className="space-y-8">
      <PageHeader
        kicker="AI Influencer"
        title="Build your AI influencer"
        description="Design a brand-aligned AI persona. Generate content, voiceovers, and campaigns from a single character."
        actions={
          <button
            data-testid="influencer-generate-btn"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
          >
            <Wand2 className="h-4 w-4" /> Generate Influencer
          </button>
        }
      />

      <section className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Character Type</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {INFLUENCER_TYPES.map((t) => (
            <button
              key={t.id}
              data-testid={`infl-type-${t.id}`}
              onClick={() => setType(t.id)}
              className={cn(
                "rounded-xl py-3 px-2 text-xs border transition",
                type === t.id
                  ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                  : "border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 p-5 flex flex-col items-center text-center" data-testid="influencer-preview">
          <div className="h-40 w-40 rounded-2xl pl-signature-gradient grid place-items-center mb-3">
            <UserSquare2 className="h-16 w-16 text-[#061414]" strokeWidth={1.4} />
          </div>
          <div className="font-display text-xl font-semibold text-[var(--pl-text)] capitalize">
            New · {type}
          </div>
          <div className="text-xs text-[var(--pl-text-muted)] mt-1">
            {gender} · {tone}
          </div>
          <div className="mt-3 flex flex-wrap gap-1 justify-center">
            <span className="text-[10px] px-2 py-0.5 rounded-md border border-[var(--pl-aqua)]/30 bg-[var(--pl-aqua)]/10 text-[var(--pl-aqua)]">
              {niche.split("·")[0]?.trim() || "premium"}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-md border border-[var(--pl-border)] text-[var(--pl-text-muted)]">
              Voice ready
            </span>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Gender / Style">
              <select
                data-testid="infl-gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
              >
                {GENDERS.map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Tone">
              <select
                data-testid="infl-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
              >
                {TONES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Content niche">
            <input
              data-testid="infl-niche"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              placeholder="e.g. high-ticket coaching · founders 30+"
              style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
              className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
            />
          </Field>

          <Field label="Personality prompt">
            <textarea
              data-testid="infl-personality"
              rows={5}
              value={personality}
              onChange={(e) => setPersonality(e.target.value)}
              placeholder="Describe how this influencer talks, thinks, and what they refuse to do."
              style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
              className="w-full rounded-xl border border-[var(--pl-border)] p-3 text-sm placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
            />
          </Field>

          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Voice</div>
            <div className="flex items-center gap-2 mt-2">
              <Mic className="h-4 w-4 text-[var(--pl-aqua)]" />
              <select
                data-testid="infl-voice"
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="flex-1 rounded-xl border border-[var(--pl-border)] px-3 h-10 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
              >
                <option>Premium · Soft</option>
                <option>Premium · Bold</option>
                <option>Editorial · Calm</option>
                <option>Founder · Confident</option>
              </select>
              <button
                data-testid="infl-preview-voice"
                className="h-10 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40 inline-flex items-center gap-1"
              >
                <Sparkles className="h-3.5 w-3.5 text-[var(--pl-aqua)]" /> Preview
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Saved Influencers</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SAVED_INFLUENCERS.map((i) => (
            <div
              key={i.id}
              data-testid={`saved-infl-${i.id}`}
              className="rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/70 pl-lift"
            >
              <div className="aspect-[4/5] bg-[var(--pl-bg-2)] overflow-hidden">
                <img src={i.thumb} alt={i.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-[var(--pl-text)]">{i.name}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] flex items-center gap-1.5 mt-1">
                  <Tag className="h-3 w-3" /> {i.niche} · {i.style}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">{label}</div>
      {children}
    </label>
  );
}
